import { isoStringToDate } from "../shared/date-format";
import { ColumnDefinition, EntityInterface } from "./Table";
import { TransactionEvent } from "./TransactionEvent";

/**
 * An Authorization is created whenever a borrower accepts a transaction request. This can happen in two ways:
 * - Borrower clicks `Accept` on CommandCenter for a request sent by a supplier
 * - Borrower approves request in Transend checkout for their cart from a supplier
 *
 * Authorizations contain the initial amount sent in the request (total_amount) and how much that
 * amount has been captured (captured_amount), voided (voided_amount), or other operations
 */

export interface AuthorizationInterface {
    id: string;                     // Unique identifier for the authorization
    buyer_company?: string;          // Buyer's company name
    buyer_id?: string;               // Buyer's company ID
    supplier_id?: string;            // Supplier's company ID
    supplier_company?: string;       // Supplier's company name
    created_at: string | null;      // Date of creation
    last_updated_at: string | null; // Date of last update (null if no updates)
    captured_amount: number;        // Amount that has been captured
    voided_amount: number;          // Amount that has been voided
    total_amount: number;           // Total authorization amount
    tx_request_id: string;          // Transaction request identifier
    transaction_ids: string[];      // List of associated transaction IDs
}

/**
 * Authorization class representing an authorization with specialized getters
 */
export class Authorization implements EntityInterface {
    /** Associated transaction events */
    private _transactionEvents?: TransactionEvent[];

    /**
     * Constructor for the Authorization class
     * 
     * @param id Unique identifier for the authorization
     * @param buyer_company Buyer's company name
     * @param buyer_id Buyer's company ID
     * @param supplier_id Supplier's company ID
     * @param supplier_company Supplier's company name
     * @param created_at Date of creation
     * @param last_updated_at Date of last update (null if no updates)
     * @param captured_amount Amount that has been captured
     * @param voided_amount Amount that has been voided
     * @param total_amount Total authorization amount
     * @param tx_request_id Transaction request identifier
     * @param transaction_ids List of associated transaction IDs
     */
    constructor(
        public id: string,
        public buyer_company: string,
        public buyer_id: string,
        public supplier_id: string,
        public supplier_company: string,
        public created_at: Date | null,
        public last_updated_at: Date | null,
        public captured_amount: number,
        public voided_amount: number,
        public total_amount: number,
        public tx_request_id: string,
    ) { }

    /**
     * Creates a copy of the current Authorization instance
     * 
     * @returns A copy of the current Authorization instance
     */
    copyOf(): Authorization {
        const copy = new Authorization(
            this.id,
            this.buyer_company,
            this.buyer_id,
            this.supplier_id,
            this.supplier_company,
            this.created_at,
            this.last_updated_at,
            this.captured_amount,
            this.voided_amount,
            this.total_amount,
            this.tx_request_id,
        );
        
        // Deep copy posted transactions if they exist
        if (this._transactionEvents) {
            copy.setTransactionEvents(this._transactionEvents.map(pt => pt.copyOf()));
        }
        
        return copy;
    }

    /**
     * Return true if the current Authorization instance is equal to the given authorization
     * 
     * @param other 
     * @returns 
     */
    equals(other: Authorization): boolean {
        return this.id === other.id &&
               this.buyer_company === other.buyer_company &&
               this.buyer_id === other.buyer_id &&
               this.supplier_id === other.supplier_id &&
               this.supplier_company === other.supplier_company &&
               this.created_at === other.created_at &&
               this.last_updated_at === other.last_updated_at &&
               this.captured_amount === other.captured_amount &&
               this.voided_amount === other.voided_amount &&
               this.total_amount === other.total_amount &&
               this.tx_request_id === other.tx_request_id;
    }

    /**
     * Returns a string representation of the Authorization instance for filtering
     * 
     * @param defs The column definitions to use for formatting
     * @returns A string representation of the Authorization instance
     */
    toString(defs: ColumnDefinition<any>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /** Posted Transaction methods */

    /**
     * Gets the transaction events for this authorization
     * 
     * @returns Array of transaction events or undefined if not loaded
     */
    getTransactionEvents(): TransactionEvent[] {
        return this._transactionEvents || [];
    }

    /**
     * Sets the transaction events for this authorization
     * 
     * @param transactions Array of transaction events
     */
    setTransactionEvents(transactions: TransactionEvent[]): void {
        this._transactionEvents = transactions;
    }

    /**
     * Adds a transaction event to this authorization
     * 
     * @param transaction The transaction event to add
     */
    addTransactionEvent(transaction: TransactionEvent): void {
        if (!this._transactionEvents) {
            this._transactionEvents = [];
        }
        this._transactionEvents.push(transaction);
    }

    /**
     * Removes a transaction event from this authorization
     * 
     * @param transactionId The ID of the transaction to remove
     * @returns The removed transaction event, or null if not found
     */
    removeTransactionEvent(transactionId: string): TransactionEvent | null {
        if (!this._transactionEvents) {
            return null;
        }
        
        const index = this._transactionEvents.findIndex(pt => pt.id === transactionId);
        if (index !== -1) {
            return this._transactionEvents.splice(index, 1)[0];
        }
        return null;
    }

    /**
     * Returns status on if an authorization has a transaction event by id
     * 
     * @param transactionId The ID of the transaction to find
     * @returns true if the transaction is found, false if not
     */
    hasTransactionEvent(transactionId: string): boolean {
        return this._transactionEvents?.some(pt => pt.id === transactionId) ?? false;
    }

    /** Special getters */

    /**
     * Gets the remaining amount by subtracting captured and voided amounts from total
     * 
     * @returns The remaining amount available for capture
     */
    get remainingAmount(): number {
        return this.total_amount - this.captured_amount - this.voided_amount;
    }

    /**
     * Checks if transaction events are loaded
     * 
     * @returns True if transaction events are loaded, false otherwise
     */
    get hasLoadedTransactions(): boolean {
        return this._transactionEvents !== undefined;
    }

    /**
     * Gets the total captured amount from transaction events (if loaded)
     * 
     * @returns Total captured amount from transactions, or undefined if not loaded
     */
    get totalCapturedFromTransactions(): number | undefined {
        if (!this._transactionEvents) {
            return undefined;
        }
        
        return this._transactionEvents
            .filter(pt => pt.status === 'captured')
            .reduce((sum, pt) => sum + pt.amount, 0);
    }

    /**
     * Gets the total voided amount from transaction events (if loaded)
     * 
     * @returns Total voided amount from transactions, or undefined if not loaded
     */
    get totalVoidedFromTransactions(): number | undefined {
        if (!this._transactionEvents) {
            return undefined;
        }
        
        return this._transactionEvents
            .filter(pt => pt.status === 'voided')
            .reduce((sum, pt) => sum + pt.amount, 0);
    }

    /**
     * Gets the count of transaction events
     * 
     * @returns Number of transaction events, or undefined if not loaded
     */
    get transactionCount(): number | undefined {
        return this._transactionEvents?.length;
    }
}

/** Mapper for converting between AuthorizationInterface and Authorization */
export class AuthorizationMapper {
    static toDto(authorization: Authorization): AuthorizationInterface {
        return {
            id: authorization.id,
            buyer_company: authorization.buyer_company,
            buyer_id: authorization.buyer_id,
            supplier_id: authorization.supplier_id,
            supplier_company: authorization.supplier_company,
            created_at: authorization.created_at?.toISOString() || null,
            last_updated_at: authorization.last_updated_at?.toISOString() || null,
            captured_amount: authorization.captured_amount,
            voided_amount: authorization.voided_amount,
            total_amount: authorization.total_amount,
            tx_request_id: authorization.tx_request_id,
            transaction_ids: authorization.getTransactionEvents().map(t => t.id),
        };
    }

    static toModel(dto: AuthorizationInterface): Authorization {
        return new Authorization(
            dto.id,
            dto.buyer_company || '',
            dto.buyer_id || '',
            dto.supplier_id || '',
            dto.supplier_company || '',
            isoStringToDate(dto.created_at),
            isoStringToDate(dto.last_updated_at),
            dto.captured_amount,
            dto.voided_amount,
            dto.total_amount,
            dto.tx_request_id,
        );
    }
}
